import React, { CSSProperties, ReactNode, useMemo, useRef, useState } from 'react';
import DataTableGovernance from '../../../components/main/DataTableGovernance/DataTableGovernance';
import GovernanceFilterBar from '../../../components/main/GovernanceFilterBar/GovernanceFilterBar';
import RefreshButton from '../../../components/main/RefreshButton/RefreshButton';
import RequestLibraryCards from '../../../components/main/RequestLibraryCards/RequestLibraryCards';
import { ContentVersionSortInput, DisciplineEnum, useGetPendingRequestsLibrarySummaryQuery, useGetPendingRequestsQuery } from '../../../generated/graphql';
import { createGraphQLOrder, TableSort } from '../../../components/main/DataTableNew/DataTableSorting';

type PendingRequestsProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
    className?: string;
};

function filterRequests(data: any[] | undefined | null, search: string) {
    if (!data || !search) return data;
    const lower = search.toLowerCase();
    return data.filter((r: any) =>
        r.name?.toLowerCase().includes(lower) ||
        r.client?.name?.toLowerCase().includes(lower) ||
        r.discipline?.toLowerCase().includes(lower) ||
        r.status?.toLowerCase().includes(lower) ||
        r.createdBy?.name?.toLowerCase().includes(lower)
    );
}

const PendingRequests = (props: PendingRequestsProps) => {
    const { style } = props;
    const [discipline, setDiscipline] = useState<DisciplineEnum | null>(null);
    const [sort, setSort] = useState<TableSort | null>({ field: 'updatedTime', direction: 'desc' });
    const [searchText, setSearchText] = useState('');
    const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
    const [selectedLibraryIds, setSelectedLibraryIds] = useState<string[]>([]);
    const loadingMore = useRef(false);

    const requests = useGetPendingRequestsQuery({
        variables: {
            discipline: discipline,
            order: createGraphQLOrder<ContentVersionSortInput>(sort, { field: 'updatedTime', direction: 'desc' }),
            where: selectedClientIds.length || selectedLibraryIds.length ? {
                clientId: selectedClientIds.length ? { in: selectedClientIds } : undefined,
                libraryId: selectedLibraryIds.length ? { in: selectedLibraryIds } : undefined,
            } : undefined,
        },
    });
    const summary = useGetPendingRequestsLibrarySummaryQuery({
        variables: { discipline },
    });

    const filteredData = useMemo(() => filterRequests(requests.data?.PendingRequests?.nodes, searchText), [requests.data, searchText]);

    const fetchMore = async () => {
        const pageInfo = requests.data?.PendingRequests?.pageInfo;
        if (!pageInfo?.hasNextPage || !pageInfo.endCursor || loadingMore.current) return;
        loadingMore.current = true;
        try {
            await requests.fetchMore({
                variables: {
                    after: pageInfo.endCursor,
                    order: createGraphQLOrder<ContentVersionSortInput>(sort, { field: 'updatedTime', direction: 'desc' }),
                    where: selectedClientIds.length || selectedLibraryIds.length ? {
                        clientId: selectedClientIds.length ? { in: selectedClientIds } : undefined,
                        libraryId: selectedLibraryIds.length ? { in: selectedLibraryIds } : undefined,
                    } : undefined,
                },
                updateQuery: (previous, { fetchMoreResult }) => {
                    if (!fetchMoreResult?.PendingRequests) return previous;
                    return {
                        ...previous,
                        PendingRequests: {
                            ...fetchMoreResult.PendingRequests,
                            nodes: [
                                ...(previous.PendingRequests?.nodes ?? []),
                                ...(fetchMoreResult.PendingRequests.nodes ?? []),
                            ],
                        },
                    };
                },
            });
        } finally {
            loadingMore.current = false;
        }
    };

    return (
        <div className="page-container governance-list-page" style={style} data-testid="GovernancePendingRequests">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Library Requests</h1>
                    <p className="page-subtitle">All open requests in the governance queue</p>
                </div>
                <RefreshButton onRefresh={() => { requests.refetch(); summary.refetch(); }} className="btn-secondary" iconSize={16} />
            </div>
            <RequestLibraryCards
                requests={summary.data?.PendingRequests?.nodes}
                selectedClientIds={selectedClientIds}
                selectedLibraryIds={selectedLibraryIds}
                onClientsSelect={setSelectedClientIds}
                onLibrariesSelect={setSelectedLibraryIds}
            />
            <GovernanceFilterBar
                discipline={discipline}
                onDisciplineChange={setDiscipline}
                isAscending={sort?.field === 'updatedTime' && sort.direction === 'asc'}
                onSortChange={(isAscending) => setSort({ field: 'updatedTime', direction: isAscending ? 'asc' : 'desc' })}
            />
            <div className="flex-1 min-h-0 relative">
                <DataTableGovernance
                    onSearchBoxChanged={setSearchText}
                    onRefetch={() => requests.refetch()}
                    fetchMore={fetchMore}
                    hasMore={requests.data?.PendingRequests?.pageInfo.hasNextPage}
                    data={filteredData}
                    isLoading={requests.loading}
                    sort={sort}
                    onSortChange={setSort}
                />
            </div>
        </div>
    );
};

export default PendingRequests;
